import React, { useState } from 'react';
import Grid2 from '@mui/material/Unstable_Grid2';
import {
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Button,
  Checkbox,
  Collapse,
  Pagination,
  CircularProgress,
  Typography,
  Box,
  IconButton,
  LinearProgress
} from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import ReviewUserModal from './ReviewUserModal';

const EnhancedUserTable = ({ users, total, loading, onUserAction, onPageChange, currentOffset, limit }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [reviewModalUser, setReviewModalUser] = useState(null);

  if (!users || users.length === 0) {
    if (loading) {
      // Show loading state
      return (
        <Card>
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2">
              User Risk Details
            </Typography>
          </Box>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>Loading users...</Typography>
          </CardContent>
        </Card>
      );
    }

    // Show empty state
    return (
      <Card>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2">
            User Risk Details
          </Typography>
        </Box>
        <CardContent sx={{ textAlign: 'center', py: 5 }}>
          <InboxIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>No users found matching current filters</Typography>
        </CardContent>
      </Card>
    );
  }

  const toggleRow = (userId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedRows(newExpanded);
  };

  const toggleUserSelection = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map(u => u.user_id)));
    }
  };

  const getRiskColor = (riskScore) => {
    if (riskScore >= 8) return 'error';
    if (riskScore >= 6) return 'warning';
    if (riskScore >= 4) return 'info';
    return 'success';
  };

  const getClassificationBadge = (classification) => {
    const config = {
      'HIGH_RISK_CREEP': { color: 'error' },
      'MODERATE_CREEP': { color: 'warning' },
      'LOW_RISK': { color: 'info' },
      'APPROPRIATE_ACCESS': { color: 'success' }
    };
    const c = config[classification] || { color: 'default' };
    return <Chip label={classification?.replace(/_/g, ' ')} color={c.color} size="small" variant="outlined" />;
  };

  const handleBulkAction = (action) => {
    if (selectedUsers.size === 0) {
      alert('Please select users first');
      return;
    }
    console.log(`Bulk action ${action} for users:`, Array.from(selectedUsers));
    // TODO: Implement bulk actions
  };

  const handleReviewAction = (action, userId, data) => {
    console.log(`Review action: ${action} for user ${userId}`, data);
    // Call parent handler if provided
    if (onUserAction) {
      onUserAction(action, userId, data);
    }
    // TODO: Update user status in backend
    // For now, just log the action
  };

  const currentPage = Math.floor(currentOffset / limit) + 1;
  const totalPages = Math.ceil((total || users.length) / limit);

  return (
    <Card>
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="subtitle2">
            User Risk Details
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Showing {currentOffset + 1}-{Math.min(currentOffset + limit, total || users.length)} of {total || users.length} users
            {selectedUsers.size > 0 && ` (${selectedUsers.size} selected)`}
          </Typography>
        </Box>
        {selectedUsers.size > 0 && (
          <Box>
            <Button
              variant="outlined"
              size="small"
              sx={{ mr: 1 }}
              onClick={() => handleBulkAction('review')}
            >
              Review Access
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => handleBulkAction('remediate')}
            >
              Remediate
            </Button>
          </Box>
        )}
      </Box>
      <Box sx={{ maxHeight: 700, overflowY: 'auto', position: 'relative', p: 0 }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>Refreshing data...</Typography>
            </Box>
          </Box>
        )}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }}>
                  <Checkbox
                    checked={selectedUsers.size === users.length}
                    onChange={toggleSelectAll}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ width: 40 }} />
                <TableCell>User</TableCell>
                <TableCell align="center">Risk Score</TableCell>
                <TableCell align="center">Classification</TableCell>
                <TableCell align="center">Groups vs Peers</TableCell>
                <TableCell align="center">Unused Apps</TableCell>
                <TableCell align="center">Confidence</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <React.Fragment key={user.user_id || user.id}>
                  <TableRow hover>
                    <TableCell>
                      <Checkbox
                        checked={selectedUsers.has(user.user_id)}
                        onChange={() => toggleUserSelection(user.user_id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleRow(user.user_id)}
                      >
                        <Typography variant="caption">
                          {expandedRows.has(user.user_id) ? '▼' : '▶'}
                        </Typography>
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{user.name || 'Unknown'}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{user.email || 'No email'}</Typography>
                        {(user.department || user.job_title) && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            {user.department || 'Unknown Dept'} {user.job_title && `• ${user.job_title}`}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ width: 60, mx: 'auto' }}>
                        <Box sx={{ position: 'relative', height: 20 }}>
                          <LinearProgress
                            variant="determinate"
                            value={(user.risk_score / 10) * 100}
                            color={getRiskColor(user.risk_score)}
                            sx={{ height: 20, borderRadius: 1 }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              color: 'common.white',
                              fontSize: '0.7rem'
                            }}
                          >
                            {user.risk_score?.toFixed(1)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {getClassificationBadge(user.classification)}
                    </TableCell>
                    <TableCell align="center">
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{user.group_count}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          (avg: {user.peer_avg_groups?.toFixed(1) || 0})
                        </Typography>
                        {user.groups_above_peer_avg > 0 && (
                          <Chip
                            label={`+${user.groups_above_peer_avg?.toFixed(0)}`}
                            color="error"
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.unused_access_count || 0}
                        color={user.unused_access_count >= 5 ? 'error' : user.unused_access_count >= 3 ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption">
                        {user.confidence ? `${(user.confidence * 100).toFixed(0)}%` : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setReviewModalUser(user)}
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>

                  {/* Expandable Row Details */}
                  <TableRow>
                    <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
                      <Collapse in={expandedRows.has(user.user_id)} timeout="auto" unmountOnExit>
                        <Box sx={{ bgcolor: 'action.hover', p: 2, borderTop: 1, borderColor: 'divider' }}>
                          <Grid2 container spacing={2}>
                            <Grid2 xs={12} md={6}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>AI Analysis</Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                {user.reasoning || 'No AI reasoning available'}
                              </Typography>
                            </Grid2>
                            <Grid2 xs={12} md={6}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>Recommendations</Typography>
                              {user.recommendations && user.recommendations.length > 0 ? (
                                <Box component="ul" sx={{ fontSize: '0.875rem', pl: 2.5, m: 0 }}>
                                  {user.recommendations.map((rec, idx) => (
                                    <li key={idx}>{rec}</li>
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  No specific recommendations
                                </Typography>
                              )}
                            </Grid2>
                          </Grid2>

                          {user.groups_to_review && user.groups_to_review.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>Groups to Review</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {user.groups_to_review.map((group, idx) => (
                                  <Chip key={idx} label={group} color="warning" size="small" />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {user.indicators && user.indicators.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>Risk Indicators</Typography>
                              <Box component="ul" sx={{ fontSize: '0.875rem', pl: 2.5, m: 0 }}>
                                {user.indicators.map((indicator, idx) => (
                                  <li key={idx}>{indicator}</li>
                                ))}
                              </Box>
                            </Box>
                          )}

                          <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Analysis Date: {new Date(user.analysis_date).toLocaleString()} |
                              Model: {user.model_used || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            size="small"
            onChange={(e, page) => onPageChange((page - 1) * limit)}
          />
        </Box>
      )}

      {/* Review Modal */}
      <ReviewUserModal
        show={!!reviewModalUser}
        onHide={() => setReviewModalUser(null)}
        user={reviewModalUser}
        onAction={handleReviewAction}
      />
    </Card>
  );
};

export default EnhancedUserTable;
